import { createSlice } from '@reduxjs/toolkit';
import simpleBackendRequest, {
    ValidationExceptionResponse,
} from './network/simpleBackendRequest';
import { API_SHORT } from '../constants';
import { RequestStatus } from './network/managedRequest';

interface ResetPasswordDto {
    confirmCode: string;
    password: string;
}

interface PasswordResetStore {
    resetTokenValidation: RequestStatus;
    startPasswordReset: RequestStatus;
    forceSetNewPasswordRequest: {
        status: RequestStatus;
        error?: string;
    };
}

const initialState: PasswordResetStore = {
    resetTokenValidation: RequestStatus.INITIAL,
    startPasswordReset: RequestStatus.INITIAL,
    forceSetNewPasswordRequest: {
        status: RequestStatus.INITIAL,
        error: undefined,
    },
};

const passwordResetSlice = createSlice({
    name: 'passwordReset',
    initialState,
    reducers: {
        setResetTokenValidation(state, action) {
            state.resetTokenValidation = action.payload;
        },
        setStartPasswordReset(state, action) {
            state.startPasswordReset = action.payload;
        },
        setForceSetNewPassword(state, action) {
            state.forceSetNewPasswordRequest = action.payload;
        },
    },
});

const {
    setResetTokenValidation,
    setStartPasswordReset,
    setForceSetNewPassword,
} = passwordResetSlice.actions;

export function VerifyResetToken(resetToken: string) {
    return async (dispatch) => {
        try {
            dispatch(setResetTokenValidation(RequestStatus.PENDING));
            const request: RequestInit = {};
            const result: Response = await simpleBackendRequest(
                `${API_SHORT}/v1/signup/verify_reset_token/${resetToken}`,
                request
            );
            if (result && result.status >= 400) {
                dispatch(setResetTokenValidation(RequestStatus.FAILURE));
            } else {
                dispatch(setResetTokenValidation(RequestStatus.SUCCESS));
            }
        } catch {
            dispatch(setResetTokenValidation(RequestStatus.FAILURE));
        }
    };
}

export function ForceSetNewPassword(data: ResetPasswordDto) {
    return async (dispatch) => {
        try {
            dispatch(setForceSetNewPassword({ status: RequestStatus.PENDING }));
            const request: RequestInit = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    confirm_code: data.confirmCode,
                    password: data.password,
                }),
            };

            const result: Response = await simpleBackendRequest(
                `${API_SHORT}/v1/signup/change_password`,
                request
            );
            if (result && result.status >= 400) {
                const errorBody: ValidationExceptionResponse =
                    await result.json();
                const errorTag =
                    errorBody.tags &&
                    errorBody.tags.length > 0 &&
                    errorBody.tags[0];
                dispatch(
                    setForceSetNewPassword({
                        status: RequestStatus.FAILURE,
                        error: errorTag || 'UNDEFINED',
                    })
                );
            } else {
                dispatch(
                    setForceSetNewPassword({ status: RequestStatus.SUCCESS })
                );
            }
        } catch {
            dispatch(
                setForceSetNewPassword({
                    status: RequestStatus.FAILURE,
                    error: 'UNDEFINED',
                })
            );
        }
    };
}

export function StartPasswordReset(email: string) {
    return async (dispatch) => {
        try {
            dispatch(setStartPasswordReset(RequestStatus.PENDING));
            const request: RequestInit = {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                }),
            };
            const result = await simpleBackendRequest(
                `${API_SHORT}/v1/signup/reset_password`,
                request
            );
            if (result && result.status >= 400) {
                dispatch(setStartPasswordReset(RequestStatus.FAILURE));
            } else {
                dispatch(setStartPasswordReset(RequestStatus.SUCCESS));
            }
        } catch {
            dispatch(setStartPasswordReset(RequestStatus.FAILURE));
        }
    };
}

export default passwordResetSlice.reducer;
